import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import Button from "@/components/common/Button";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import { useLoader } from "@/hooks/LoaderProvider";
import { Switch, ArrowRightOutlined } from "@/config/antd";
import ExpenseItemsList from "@/pages/finance_manager/components/Expense_Item_List";
import WebIcon from "@/components/common/Web_Icons";
import Breadcrumb from "@/components/specific/Breadcrumb";
import {
  createExpense,
  updateExpense,
  fetchExpenseByAny,
  selectExpenseLoading,
  selectExpenseError,
} from "@/store/redux/slices/finance_management/expense_slice";
import {
  fetchAccountByAny,
  selectAllAccounts,
  selectFetchedAccounts,
} from "@/store/redux/slices/finance_management/account_slice";
import Steps from "@/components/common/Steps";
import { getOrganizationId } from "@/utils/token_functions";
import LocationInput from "@/components/common/inputs/Location_Input";

const ExpenseForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();

  // Redux selectors
  const isLoading = useSelector(selectExpenseLoading);
  const expenseError = useSelector(selectExpenseError);
  const accounts = useSelector(selectAllAccounts);

  // Form State
  const [formData, setFormData] = useState({
    ...(isEditMode && { _id: "" }),
    name: "",
    description: "",
    category: "",
    merchantName: "",
    merchantEmail: "",
    merchantAddress: "",
    paymentMethod: "",
    paymentStatus: "",
    isInvoice: false,
    debitAccount: "",
    paymentMode: "",
    items: [],
  });

  const [items, setItems] = useState([]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Steps related state
  const formSections = [
    { title: "Transaction Information" },
    { title: "Merchants Information" },
    { title: "Items to be Purchased" },
    { title: "Transaction Channel" },
  ];

  const [currentSection, setCurrentSection] = useState(0);
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  const sectionRefs = useRef([]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount to set initial state

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Check if sections are completed
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Transaction Information
    if (formData.name && formData.description && formData.category) {
      completed.push(0);
    }

    // Section 1: Merchant Information
    if (
      formData.merchantName &&
      formData.merchantEmail &&
      formData.merchantAddress
    ) {
      completed.push(1);
    }

    // Section 2: Items
    if (
      items.length > 0 &&
      items.every((item) => item.item && item.quantity && item.price)
    ) {
      completed.push(2);
    }

    // Section 3: Payment Details
    if (formData.debitAccount && formData.paymentMode) {
      completed.push(3);
    }

    setSectionsCompleted(completed);
  }, [formData, items]);

  // Run section completion check when form data changes
  useEffect(() => {
    checkSectionCompletion();
  }, [formData, items, checkSectionCompletion]);

  // Fetch expense data if in edit mode
  useEffect(() => {
    const fetchExpenseData = async () => {
      if (isEditMode) {
        try {
          showLoader();
          const result = await dispatch(
            fetchExpenseByAny({ key: "_id", value: id })
          ).unwrap();

          // The data is in result.data[0], not result.payload
          if (result && result.data && result.data.length > 0) {
            const expenseData = result.data[0];

            // Set the items separately
            const transformedItems =
              expenseData.items?.map((item) => ({
                id: Date.now() + Math.random(),
                item: item?.item,
                quantity: Number(item?.quantity),
                price: Number(item?.price),
              })) || [];
            setItems(transformedItems);

            setFormData({
              _id: expenseData._id,
              name: expenseData.name,
              description: expenseData.description,
              category: expenseData.itemCategoryID,
              merchantName: expenseData.merchantInformation?.name || "",
              merchantEmail: expenseData.merchantInformation?.email || "",
              merchantAddress: expenseData.merchantInformation?.location || "",
              debitAccount:
                expenseData.transactionChannel?.debitingAccountID || "",
              paymentMode:
                expenseData.transactionChannel?.modeOfPaymentID || "",
              isInvoice: expenseData.isInvoice || false,
            });
          }
        } catch (error) {
          console.error("Failed to fetch expense data:", error);
        } finally {
          hideLoader();
        }
      }
    };

    fetchExpenseData();
  }, [dispatch, hideLoader, id, isEditMode, showLoader]);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        showLoader();
        const organizationId = getOrganizationId();

        // Dispatch the action to fetch accounts by organization ID
        await dispatch(
          fetchAccountByAny({
            key: "organizationID",
            value: organizationId,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        hideLoader();
      }
    };

    fetchAccountData();
  }, [dispatch, hideLoader, showLoader]);

  // Debug the accounts data

  const accountOptions =
    Array.isArray(accounts) && accounts.length > 0
      ? accounts.map((account) => ({
          label: `${account.bankName || "Unknown Bank"} - ${
            account.accountNumber || "No Account Number"
          }`,
          value: account._id,
        }))
      : [];

  // If you have fetchedAccounts instead of accounts
  const fetchedAccounts = useSelector(selectFetchedAccounts);

  // Try using fetchedAccounts instead if accounts is empty
  const finalAccountOptions =
    accountOptions.length > 0
      ? accountOptions
      : Array.isArray(fetchedAccounts) && fetchedAccounts.length > 0
      ? fetchedAccounts.map((account) => ({
          label: `${account.bankName || "Unknown Bank"} - ${
            account.accountNumber || "No Account Number"
          }`,
          value: account._id,
        }))
      : [];

  // Payment mode options
  const paymentModeOptions = [
    { value: "transfer", label: "Bank Transfer" },
    { value: "card", label: "Card Payment" },
  ];

  // Validation check
  useEffect(() => {
    const isValid =
      formData.name?.trim() !== "" &&
      formData.description?.trim() !== "" &&
      formData.category?.trim() !== "" &&
      formData.merchantName?.trim() !== "" &&
      formData.merchantEmail?.trim() !== "" &&
      formData.merchantAddress?.trim() !== "" &&
      formData.debitAccount?.trim() !== "" &&
      formData.paymentMode?.trim() !== "" &&
      items.length > 0 &&
      items.every(
        (item) =>
          item.item?.trim() !== "" &&
          Number(item.quantity) > 0 &&
          Number(item.price) > 0
      );

    setIsFormValid(isValid);
  }, [formData, items]);

  // Handle individual item changes from ExpenseItem component
  const handleItemChange = (updatedItem) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
  };

  // Handle adding new items from ExpenseItemsList component
  const handleAddItem = () => {
    setItems((prevItems) => [
      ...prevItems,
      {
        id: Date.now(),
        item: "",
        quantity: 1,
        price: "",
      },
    ]);
  };

  const handleProceed = async () => {
    showLoader();
    try {
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [],
      });

      // Transform items to match backend structure
      const transformedItems = items.map((item) => ({
        item: item.item,
        quantity: Number(item.quantity),
        price: Number(item.price || 0),
      }));

      // Calculate grand total
      const grandTotal = items.reduce(
        (sum, item) => sum + Number(item.quantity) * Number(item.price || 0),
        0
      );

      // Prepare data for submission
      const expenseData = {
        // Required fields from backend structure
        name: formData.name,
        description: sanitizedDescription,
        itemCategoryID: formData.category,
        merchantInformation: {
          name: formData.merchantName,
          email: formData.merchantEmail,
          location: formData.merchantAddress,
          // phone is optional in your form but expected by backend
          phone: "",
        },
        items: transformedItems,
        grandTotal,
        isInvoice: formData.isInvoice,
        transactionChannel: {
          debitingAccountID: formData.debitAccount,
          modeOfPaymentID: formData.paymentMode,
        },
        // Optional fields that backend expects
        vendorID: "", // Add if you have vendor information
        campaignID: "", // Add if this is related to a campaign
        amount: grandTotal, // Using grandTotal as amount
        type: formData.category, // Using category as type
      };

      if (isEditMode) {
        await dispatch(
          updateExpense({ ...expenseData, _id: formData._id })
        ).unwrap();
      } else {
        await dispatch(createExpense(expenseData)).unwrap();
      }

      setShowSuccess(true);
    } catch (error) {
      setErrorMessage(
        error?.message ||
          `Failed to ${isEditMode ? "update" : "create"} expense`
      );
      setShowError(true);
      console.error("Error:", error);
    } finally {
      hideLoader();
    }
  };

  const handleInputChange = (field) => (e) => {
    let value;

    // Handle different types of input changes
    if (e === null) {
      value = ""; // Handle clearing of select inputs
    } else if (typeof e === "object" && e?.target) {
      value = e.target.value; // Handle regular input changes
    } else {
      value = e; // Handle select input changes
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const categoryOptions = [
    { value: "marketing", label: "Marketing" },
    { value: "operations", label: "Operations" },
    { value: "logistics", label: "Logistics" },
  ];

  const handleSuccessProceed = () => {
    showLoader();
    setShowSuccess(false);
    setTimeout(() => {
      hideLoader();
      if (isEditMode) {
        navigate(`/dashboard/financial/transaction/${id}`);
      } else {
        navigate("/dashboard/financial");
      }
    }, 2000);
  };

  useEffect(() => {
    if (expenseError) {
      setShowError(true);
      setErrorMessage(
        expenseError.message ||
          "An error occurred while saving the expense. Please try again."
      );
    }
  }, [expenseError]);

  if (isEditMode && isLoading) {
    return null;
  }

  // Define breadcrumb items
  const breadcrumbItems = [
    { title: "Financial Manager", link: "/dashboard/financial" },
    { title: "Add Expense", link: null },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left lg:grid sm:py-0">
      <div className="col-span-3 lg:border-r">
        <Breadcrumb items={breadcrumbItems} />
        {/* Back Navigation */}
        <Back text="Back" />

        <div className="my-5 rounded-lg lg:p-5 lg:border">
          {/* Header Section */}
          <div className="mb-4">
            <h1 className="heading-medium text-lighterGrey">
              {isEditMode ? "Edit Expense" : "Add a Expense to your Account"}
            </h1>
            <p className="mt-2 text-small text-lighterGrey">
              Fill the form below. All fields are required to be filled with
              accurate information.
            </p>
          </div>

          {/* Expense Details Section */}
          <div
            ref={(element) => {
              sectionRefs.current[0] = element;
            }}
            className="mt-8">
            <h2 className="mb-6 text-bold text-lighterGrey">
              Describe what this transaction is about
            </h2>

            <div className="space-y-6">
              <Input
                label="TRANSACTION NAME"
                placeholder="Enter transaction name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange("name")}
              />

              <TextEditor
                label="TRANSACTION DESCRIPTION"
                placeholder="Enter transaction description"
                value={formData.description}
                onChange={handleEditorChange}
              />

              <Input
                type="select"
                label="ITEMS CATEGORY"
                placeholder="Select category"
                value={formData.category}
                onChange={handleInputChange("category")}
                options={categoryOptions}
              />
            </div>
          </div>

          {/* Merchant's Information Section */}
          <div
            ref={(element) => {
              sectionRefs.current[1] = element;
            }}
            className="mt-10">
            <h2 className="text-bold text-lighterGrey">
              Merchant&apos;s Information
            </h2>
            <p className="mt-2 mb-6 text-regular text-lighterGrey">
              From whom are the items being purchased?
            </p>

            <div className="space-y-6">
              <Input
                label="MERCHANT'S NAME"
                placeholder="Enter merchant name"
                type="text"
                name="merchantName"
                value={formData.merchantName}
                onChange={handleInputChange("merchantName")}
              />

              <Input
                label="MERCHANT's EMAIL ADDRESS"
                placeholder="Enter merchant email"
                type="email"
                name="merchantEmail"
                value={formData.merchantEmail}
                onChange={handleInputChange("merchantEmail")}
                icon={<WebIcon icon="Message" size="small" />}
              />

              <LocationInput
                label="MERCHANT'S LOCATION"
                placeholder="Enter merchant location"
                value={formData.merchantAddress}
                onChange={handleInputChange("merchantAddress")}
                id="merchant-location"
              />
            </div>
          </div>

          {/* Items Section */}
          <div
            ref={(element) => {
              sectionRefs.current[2] = element;
            }}
            className="mt-10">
            <ExpenseItemsList
              items={items}
              onItemChange={handleItemChange}
              onAddItem={handleAddItem}
              className="mt-10 text-lighterGrey"
            />
          </div>

          {/* Payment Details Section */}
          <div
            ref={(element) => {
              sectionRefs.current[3] = element;
            }}
            className="mt-10">
            <h2 className="text-bold text-lighterGrey">Payment Details</h2>
            <p className="mt-2 mb-6 text-regular text-lighterGrey">
              Add payment information for this expense
            </p>

            <div className="space-y-6">
              <Input
                type="select"
                label="DEBITING ACCOUNT"
                placeholder="Select payment method"
                value={formData.debitAccount}
                onChange={handleInputChange("debitAccount")}
                options={finalAccountOptions}
              />

              <Input
                type="select"
                label="MODE OF PAYMENT"
                placeholder="Select payment status"
                value={formData.paymentMode}
                onChange={handleInputChange("paymentMode")}
                options={paymentModeOptions}
              />

              {/* Invoice/Receipt Toggle */}
              <div className="flex items-center space-x-3">
                <Switch
                  checked={formData.isInvoice}
                  onChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      isInvoice: checked,
                    }))
                  }
                  className="bg-darkGrey"
                />
                <span className="font-bold text-small text-darkGrey">
                  Is this an invoice or a receipt?
                </span>
              </div>
            </div>

            {/* Proceed Button */}
            <Button
              text="Proceed"
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              disabled={!isFormValid}
              color="primary"
              className={`
                w-full mt-8
                ${!isFormValid && "bg-darkBlue text-primary cursor-not-allowed"}
              `}
              customBgColor={isFormValid ? "primary" : "darkBlue"}
              customTextColor={isFormValid ? "white" : "darkBlue"}
            />
          </div>
        </div>
      </div>
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <Steps
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>

      {/* Success Dialog */}
      <Success
        isVisible={showSuccess}
        onClose={handleSuccessProceed}
        header={
          isEditMode
            ? "Expense Updated Successfully"
            : "Expense Created Successfully"
        }
        paragraph={
          isEditMode
            ? "Your expense has been successfully updated."
            : "Your expense has been successfully created."
        }
        buttonText="Done"
      />

      {/* Error Dialog */}
      <Error
        isVisible={showError}
        onClose={() => setShowError(false)}
        header="Failed to Save Expense"
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/dashboard/financial/add-expense"
      />
    </div>
  );
};

export default ExpenseForm;
